import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UserPlus, Users, Shield, Trash2, X, Mail, ChevronDown } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLE_COLORS = {
  event_manager: "bg-[#0099CC]/20 text-[#0099CC]",
  receptionist: "bg-[#10B981]/20 text-[#10B981]",
  viewer: "bg-[#8B5CF6]/20 text-[#8B5CF6]",
};

export default function OrgTeamPage() {
  const { token } = useAuth();
  const h = { Authorization: `Bearer ${token}` };
  const [members, setMembers] = useState([]);
  const [roles, setRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "viewer" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/event/team`, { headers: h });
      setMembers(r.data.members || []);
      setRoles(r.data.roles || {});
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const invite = async () => {
    if (!form.email) { toast.error("Email requis"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/api/event/team/invite`, form, { headers: h });
      toast.success("Invitation envoyee !");
      setShowInvite(false);
      setForm({ name: "", email: "", role: "viewer" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setSaving(false); }
  };

  const changeRole = async (memberId, role) => {
    try {
      await axios.put(`${API}/api/event/team/${memberId}/role`, { role }, { headers: h });
      toast.success("Role mis a jour");
      load();
    } catch { toast.error("Erreur"); }
  };

  const remove = async (memberId) => {
    if (!window.confirm("Retirer ce membre ?")) return;
    try {
      await axios.delete(`${API}/api/event/team/${memberId}`, { headers: h });
      toast.success("Membre retire");
      load();
    } catch { toast.error("Erreur"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0099CC]" /></div>;

  return (
    <div className="space-y-6" data-testid="team-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Mon Equipe</h1>
          <p className="text-[#64748b] text-sm mt-1">Gerez les roles et permissions de votre equipe evenementielle</p>
        </div>
        <Button data-testid="invite-btn" onClick={() => setShowInvite(true)} className="bg-[#0099CC] hover:bg-[#0088B5] text-white">
          <UserPlus className="h-4 w-4 mr-2" /> Inviter
        </Button>
      </div>

      {/* Role legend */}
      <div className="grid sm:grid-cols-3 gap-3">
        {[
          { role: "event_manager", icon: "M", perms: "Evenements, participants, exports, badges" },
          { role: "receptionist", icon: "R", perms: "Scanner QR, liste participants, statistiques" },
          { role: "viewer", icon: "V", perms: "Lecture seule: evenements, participants, stats" },
        ].map(r => (
          <div key={r.role} className="bg-[#0B0F19] border border-[#1E293B] rounded-xl p-3">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${ROLE_COLORS[r.role]}`}>{roles[r.role] || r.role}</span>
            </div>
            <p className="text-xs text-[#64748b]">{r.perms}</p>
          </div>
        ))}
      </div>

      {/* Invite modal */}
      {showInvite && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
          <div className="bg-[#0F1420] border border-[#2A3441] rounded-2xl w-full max-w-md p-6 space-y-4" data-testid="invite-modal">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-white">Inviter un membre</h2>
              <button onClick={() => setShowInvite(false)}><X className="h-5 w-5 text-[#64748b] hover:text-white" /></button>
            </div>
            <div className="space-y-3">
              <div><label className="text-xs text-[#64748b] mb-1 block">Nom</label><Input data-testid="invite-name" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} placeholder="Jean Dupont" className="bg-[#151B2B] border-[#2A3441] text-white" /></div>
              <div><label className="text-xs text-[#64748b] mb-1 block">Email *</label><Input data-testid="invite-email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="jean@example.com" type="email" className="bg-[#151B2B] border-[#2A3441] text-white" /></div>
              <div><label className="text-xs text-[#64748b] mb-1 block">Role</label>
                <select data-testid="invite-role" value={form.role} onChange={e => setForm(f => ({...f, role: e.target.value}))} className="w-full bg-[#151B2B] border border-[#2A3441] text-white rounded-lg px-3 py-2 text-sm">
                  <option value="event_manager">Gestionnaire d'evenements</option>
                  <option value="receptionist">Receptionniste (scan)</option>
                  <option value="viewer">Lecteur (statistiques)</option>
                </select>
              </div>
            </div>
            <div className="flex gap-3">
              <Button data-testid="send-invite-btn" onClick={invite} disabled={saving} className="bg-[#0099CC] hover:bg-[#0088B5] text-white flex-1">{saving ? "Envoi..." : "Envoyer l'invitation"}</Button>
              <Button variant="outline" onClick={() => setShowInvite(false)} className="border-[#2A3441] text-[#94A3B8]">Annuler</Button>
            </div>
          </div>
        </div>
      )}

      {/* Members list */}
      <div className="bg-[#0B0F19] border border-[#1E293B] rounded-xl overflow-hidden">
        {members.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="h-10 w-10 text-[#2A3441] mx-auto mb-3" />
            <p className="text-[#64748b]">Aucun membre dans l'equipe</p>
            <p className="text-[#4A5568] text-xs mt-1">Invitez des collaborateurs pour gerer vos evenements ensemble.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="border-b border-[#1E293B] text-[#64748b] text-xs uppercase tracking-wider">
              <th className="text-left p-3 pl-4">Membre</th>
              <th className="text-left p-3 hidden sm:table-cell">Email</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3 hidden md:table-cell">Statut</th>
              <th className="text-right p-3 pr-4">Actions</th>
            </tr></thead>
            <tbody>
              {members.map(m => (
                <tr key={m.id} className="border-b border-[#1E293B]/50 hover:bg-[#0F1420] transition-colors">
                  <td className="p-3 pl-4">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-[#0099CC]/20 flex items-center justify-center text-[#0099CC] text-xs font-bold">
                        {(m.name || m.email)?.[0]?.toUpperCase() || "?"}
                      </div>
                      <div>
                        <div className="font-medium text-white">{m.name || "—"}</div>
                        <div className="text-xs text-[#64748b] sm:hidden">{m.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-[#94A3B8] hidden sm:table-cell">{m.email}</td>
                  <td className="p-3">
                    <select value={m.role} onChange={e => changeRole(m.id, e.target.value)}
                      className={`text-xs px-2 py-1 rounded-lg bg-transparent border border-[#2A3441] ${ROLE_COLORS[m.role] || "text-white"}`}
                      data-testid={`role-select-${m.id}`}
                    >
                      <option value="event_manager" className="bg-[#0F1420] text-white">Gestionnaire</option>
                      <option value="receptionist" className="bg-[#0F1420] text-white">Receptionniste</option>
                      <option value="viewer" className="bg-[#0F1420] text-white">Lecteur</option>
                    </select>
                  </td>
                  <td className="p-3 hidden md:table-cell">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${m.status === "active" ? "bg-[#10B981]/20 text-[#10B981]" : "bg-[#F59E0B]/20 text-[#F59E0B]"}`}>
                      {m.status === "active" ? "Actif" : "Invite"}
                    </span>
                  </td>
                  <td className="p-3 pr-4 text-right">
                    <button onClick={() => remove(m.id)} className="text-red-400 hover:text-red-300 p-1.5 rounded transition-colors" data-testid={`remove-${m.id}`}>
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
